import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Plus, Mail, Edit3, Trash2, Copy } from 'lucide-react';
import {
  useEmailTemplates,
  useDeleteEmailTemplate,
  useCloneEmailTemplate,
  findFlowsUsingTemplate,
  type EmailTemplate,
} from '~/lib/automations-queries';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { CloneDialog } from '~/components/CloneDialog/CloneDialog';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { useTenantContext } from '~/lib/tenantContext';
import styles from './_authed.automations.templates.module.css';

export const Route = createFileRoute('/_authed/automations/templates')({
  component: TemplatesListPage,
});

function TemplatesListPage() {
  const navigate = useNavigate();
  const { selectedTenantId, selectedTenant, tenants } = useTenantContext();
  const { data: templates, isLoading } = useEmailTemplates();
  const del = useDeleteEmailTemplate();
  const clone = useCloneEmailTemplate();
  const confirm = useConfirm();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cloning, setCloning] = useState<EmailTemplate | null>(null);
  const [includeGlobal, setIncludeGlobal] = useState<boolean>(true);
  const [actionError, setActionError] = useState<string | null>(null);

  function handleClone(targetTenant: string) {
    if (!cloning) return;
    clone.mutate(
      { source: cloning, targetTenant },
      {
        onSuccess: (created) => {
          setCloning(null);
          navigate({
            to: '/automations/templates/$templateId',
            params: { templateId: created.id },
          });
        },
      },
    );
  }

  // Filtrado client-side: SIEMPRE filtra por el tenant activo del context.
  // Las plantillas globales (tenant='') aparecen junto a las del tenant a
  // menos que el usuario desmarque el checkbox "Incluir globales".
  const filtered = useMemo(() => {
    if (!templates) return [];
    return templates.filter((t) => {
      if (!t.tenant) return includeGlobal; // global
      return t.tenant === selectedTenantId;
    });
  }, [templates, selectedTenantId, includeGlobal]);

  async function handleDelete(id: string, name: string) {
    let usedBy: { id: string; name: string }[] = [];
    try { usedBy = await findFlowsUsingTemplate(id); } catch { /* ignore */ }
    const ok = await confirm({
      title: `Eliminar plantilla "${name}"`,
      description: (
        <>
          {usedBy.length > 0 ? (
            <p style={{ marginTop: 0, fontWeight: 600 }}>
              ⚠️ La usa{usedBy.length > 1 ? 'n' : ''} {usedBy.length} flujo
              {usedBy.length > 1 ? 's' : ''}: {usedBy.map((f) => `«${f.name}»`).join(', ')}.
              Esos pasos fallarán al ejecutarse (plantilla no encontrada).
            </p>
          ) : (
            <>Ningún flujo la usa actualmente. </>
          )}
          Esta acción no se puede deshacer.
        </>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingId(id);
    setActionError(null);
    del.mutate(id, {
      onError: (err) => setActionError('No se pudo eliminar la plantilla: ' + ((err as { message?: string }).message || 'error desconocido')),
      onSettled: () => setDeletingId(null),
    });
  }

  return (
    <div className={styles.tab}>
      <div className={styles.tabHeader}>
        <Link to="/automations/templates/new" className={styles.primaryBtn}>
          <Plus size={14} aria-hidden /> Nueva plantilla
        </Link>
      </div>

      {actionError && (
        <p role="alert" style={{ background: 'color-mix(in srgb, #dc2626 12%, transparent)', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 14, margin: '0 0 12px' }}>
          {actionError}
        </p>
      )}

      <div className={styles.toolbar}>
        <label className={styles.filterField} style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeGlobal}
            onChange={(e) => setIncludeGlobal(e.target.checked)}
          />
          <span className={styles.filterLabel}>Incluir plantillas globales del sistema</span>
        </label>
        {templates && (
          <span className={styles.countBadge}>
            {filtered.length} {selectedTenant ? `de ${selectedTenant.name}` : ''}
            {includeGlobal && ' + globales'}
          </span>
        )}
      </div>

      {isLoading && <div className={styles.empty}>Cargando templates…</div>}

      {!isLoading && (!templates || templates.length === 0) && (
        <div className={styles.emptyState}>
          <Mail size={28} aria-hidden style={{ marginBottom: 8, opacity: 0.5 }} />
          <p>
            No hay plantillas todavía. Crea la primera con el botón{' '}
            <strong>"Nueva plantilla"</strong>.
          </p>
          <p className="subtle" style={{ marginTop: 8 }}>
            Las plantillas se escriben en Markdown con variables{' '}
            <code>{'{{contact.first_name}}'}</code> que se reemplazan al
            momento de enviar.
          </p>
        </div>
      )}

      {!isLoading && templates && templates.length > 0 && filtered.length === 0 && (
        <div className={styles.emptyState}>
          <p>No hay plantillas que coincidan con el filtro.</p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Asunto</th>
              <th>Última edición</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              return (
                <tr key={t.id} className={deletingId === t.id ? styles.rowDeleting : ''}>
                  <td>
                    <Tooltip content={<>Slug: <code>{t.slug}</code></>}>
                      <Link
                        to="/automations/templates/$templateId"
                        params={{ templateId: t.id }}
                        className={styles.templateName}
                      >
                        <Mail size={14} aria-hidden style={{ opacity: 0.5 }} />
                        {t.name}
                        {!t.tenant && (
                          <span className={styles.globalChip} title="Plantilla global del sistema (disponible en todos los clientes)">
                            Global
                          </span>
                        )}
                      </Link>
                    </Tooltip>
                  </td>
                  <td className={styles.subjectCell}>{t.subject || <em className="subtle">—</em>}</td>
                  <td className="subtle">{formatDate(t.updated)}</td>
                  <td className={styles.actions}>
                    <Link
                      to="/automations/templates/$templateId"
                      params={{ templateId: t.id }}
                      className={styles.iconBtn}
                      title="Editar"
                    >
                      <Edit3 size={14} aria-hidden />
                    </Link>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => setCloning(t)}
                      title="Duplicar"
                      aria-label={`Duplicar plantilla ${t.name}`}
                    >
                      <Copy size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => handleDelete(t.id, t.name)}
                      title="Eliminar"
                      aria-label={`Eliminar plantilla ${t.name}`}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {cloning && (
        <CloneDialog
          title="Duplicar plantilla"
          itemName={cloning.name}
          sourceTenantId={cloning.tenant}
          tenants={tenants}
          allowGlobal
          description={
            <>
              Se creará una copia de <strong>{cloning.name}</strong> en el
              destino que elijas (un cliente o la library global del sistema).
            </>
          }
          isPending={clone.isPending}
          onClose={() => setCloning(null)}
          onClone={handleClone}
          crossTenantNote={
            <>
              Las plantillas no tienen referencias propias del cliente: el
              contenido (asunto, cuerpo, variables <code>{'{{...}}'}</code>) se
              copia tal cual. Solo se ajusta el slug para no chocar en el
              destino. Útil para partir de una global y personalizarla por
              tenant.
            </>
          }
        />
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
